import { useCallback, useEffect, useState } from 'react';

// Minimal path routing for the three SPAs (a handful of routes each; no router dependency).
export function usePath(): [string, (to: string, opts?: { replace?: boolean }) => void] {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const go = useCallback((to: string, opts: { replace?: boolean } = {}) => {
    // Each in-app step remembers the page it came from (path and filters), so a detail page's back link can return to
    // the list exactly as it was left (BackLink).
    const from = `${window.location.pathname}${window.location.search}`;
    if (opts.replace) window.history.replaceState(window.history.state, '', to); else window.history.pushState({ from }, '', to);
    setPath(to.split('?')[0]!.split('#')[0]!);
    window.scrollTo?.(0, 0);
  }, []);
  return [path, go];
}

/** matchPath('/applications/:id', '/applications/abc') → { id: 'abc' } */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i]!.startsWith(':')) out[p[i]!.slice(1)] = decodeURIComponent(a[i]!);
    else if (p[i] !== a[i]) return null;
  }
  return out;
}

/** The page this one was opened from inside the app ("/alerts?states=L1_TRIAGE"), or null after a reload or a link. */
export const cameFrom = (): string | null => (window.history.state as { from?: string } | null)?.from ?? null;

/**
 * A list filter kept in the address (?states=…), so it survives opening an item and coming back, a refresh and a shared
 * link. Arrays are stored comma-separated; the default is not written to the address.
 */
export function useSearchParam(key: string, initial: string): [string, (v: string) => void] {
  const [value, setValue] = useState(() => new URLSearchParams(window.location.search).get(key) ?? initial);
  const set = useCallback((v: string) => {
    setValue(v);
    const q = new URLSearchParams(window.location.search);
    if (v === initial) q.delete(key); else q.set(key, v);
    const qs = q.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, [key, initial]);
  return [value, set];
}
